/**
 * `(figure, t) → pixels`, on a Canvas 2D context. A port of
 * app/lib/render/bdl_painter.dart: same keyframe semantics, same role →
 * colour map, same style constants. If the two ever disagree, the Dart
 * painter is right.
 */
import { flatten, pointAt, prefix, type Flat, type Subpath } from "./geometry";
import { centroid, type Figure, type Part, type Role, type Track, type Verb } from "./model";

// Mirrors app/lib/design/colors.dart + figure_style.dart.
const C = {
  paper: "#f4efe4", paperDeep: "#eae3d4", ink: "#2e2a26", inkSoft: "#6b635a",
  orange: "#e09b4a", orangeLight: "#f2c994", orangeDeep: "#c77e2e",
  blue: "#6b9ab8", blueLight: "#b4cfdf", blueDeep: "#4a7794",
  green: "#85a76f", greenLight: "#c0d4b0", greenDeep: "#62854d",
};
const STYLE = { structureStroke: 1.5, roleStroke: 1.3, leaderStroke: 0.45, particleRadius: 1.6, labelSize: 3.2, labelMinPx: 10, labelTracking: 0.08, captionSize: 3.8, captionMinPx: 11, captionBand: 9 };

export const stroke = (role: Role, emphasis = false): string =>
  role === "structure" ? C.ink
  : role === "driver" ? (emphasis ? C.orangeDeep : C.orange)
  : role === "medium" ? (emphasis ? C.blueDeep : C.blue)
  : emphasis ? C.greenDeep : C.green;

export const fill = (role: Role, emphasis = false): string =>
  role === "structure" ? C.paperDeep
  : role === "driver" ? (emphasis ? C.orange : C.orangeLight)
  : role === "medium" ? (emphasis ? C.blue : C.blueLight)
  : emphasis ? C.green : C.greenLight;

const particle = (role: Role) => stroke(role, true);

const curves: Record<string, (t: number) => number> = {
  linear: (t) => t,
  easeIn: (t) => t * t * t,
  easeOut: (t) => 1 - Math.pow(1 - t, 3),
  easeInOut: (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
};

export interface PartState {
  translate: [number, number]; rotate: number; scale: number; pivot: [number, number];
  opacity: number; fillLevel: number; reveal: number; hidden: boolean;
}

interface Resolved {
  parts: Array<{ part: Part; flat: Flat }>;
  flows: Array<{ part: Part; flat: Flat; track: Track }>;
  tracks: Map<string, Map<Verb, Track[]>>;
}

const cache = new WeakMap<Figure, Resolved>();

export function resolve(fig: Figure): Resolved {
  const hit = cache.get(fig);
  if (hit) return hit;
  const parts = [...fig.parts]
    .sort((a, b) => (a.z ?? 0) - (b.z ?? 0))
    .map((part) => ({ part, flat: flatten(part.shape, part.geometry) }));
  const byId = new Map(parts.map((p) => [p.part.id, p]));
  const tracks = new Map<string, Map<Verb, Track[]>>();
  const flows: Resolved["flows"] = [];
  for (const t of fig.timeline?.tracks ?? []) {
    const targets = Array.isArray(t.target) ? t.target : [t.target];
    for (const id of targets) {
      const p = byId.get(id);
      if (!p) continue;
      if (t.verb === "flowAlong") { flows.push({ part: p.part, flat: p.flat, track: t }); continue; }
      const m = tracks.get(id) ?? new Map<Verb, Track[]>();
      const list = m.get(t.verb) ?? [];
      list.push(t);
      m.set(t.verb, list);
      tracks.set(id, m);
    }
  }
  for (const m of tracks.values()) for (const list of m.values()) list.sort((a, b) => (a.start ?? 0) - (b.start ?? 0));
  const r = { parts, flows, tracks };
  cache.set(fig, r);
  return r;
}

/** Keyframe semantics — see the Dart painter's `_valueAt`. */
export function valueAt(sorted: Track[], ms: number, verb: Verb): number | [number, number] {
  const dflt = verb === "scale" ? 1 : verb === "translate" ? ([0, 0] as [number, number]) : 0;
  let result = sorted[0]?.from ?? dflt;
  for (const t of sorted) {
    const start = t.start ?? 0, dur = t.duration ?? 0, end = start + dur;
    if (ms >= end) { result = t.to ?? dflt; }
    else if (ms >= start) {
      const p = curves[t.curve ?? "linear"]!(dur === 0 ? 1 : Math.min(1, Math.max(0, (ms - start) / dur)));
      const a = t.from ?? dflt, b = t.to ?? dflt;
      if (verb === "toggle") result = b;
      else if (Array.isArray(a) && Array.isArray(b)) result = [a[0] + (b[0] - a[0]) * p, a[1] + (b[1] - a[1]) * p];
      else result = (a as number) + ((b as number) - (a as number)) * p;
      break;
    } else break;
  }
  return result;
}

export function stateAt(r: Resolved, part: Part, ms: number): PartState {
  const s: PartState = { translate: [0, 0], rotate: 0, scale: 1, pivot: centroid(part.geometry), opacity: 1, fillLevel: 1, reveal: 1, hidden: false };
  const byVerb = r.tracks.get(part.id);
  if (!byVerb) return s;
  let pivot: [number, number] | undefined;
  for (const [verb, list] of byVerb) {
    const v = valueAt(list, ms, verb);
    switch (verb) {
      case "translate": s.translate = v as [number, number]; break;
      case "rotate": s.rotate = v as number; pivot ??= list.find((t) => t.pivot)?.pivot; break;
      case "scale": s.scale = v as number; pivot ??= list.find((t) => t.pivot)?.pivot; break;
      case "fillLevel": s.fillLevel = v as number; break;
      case "pulse": s.opacity = 0.25 + 0.75 * Math.min(1, Math.max(0, v as number)); break;
      case "toggle": s.hidden = (v as number) < 0.5; break;
      case "revealAlong": s.reveal = Math.min(1, Math.max(0, v as number)); break;
      default: break;
    }
  }
  if (pivot) s.pivot = pivot;
  return s;
}

function trace(ctx: CanvasRenderingContext2D, subpaths: Subpath[]): void {
  ctx.beginPath();
  for (const sp of subpaths) {
    sp.pts.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
    if (sp.closed) ctx.closePath();
  }
}

export interface PaintOptions { labelMinPx?: number; background?: string | null }

/** Paints one frame at normalised time t (0..1) into a size×size box. */
export function paintFigure(ctx: CanvasRenderingContext2D, fig: Figure, width: number, height: number, t: number, opts: PaintOptions = {}): void {
  const r = resolve(fig);
  const [vbx, vby] = fig.viewBox ?? [100, 100];
  // A captioned figure reserves a band under the viewBox for its caption (as the Dart painter does).
  const band = fig.caption ? STYLE.captionBand : 0;
  const scale = Math.min(width / vbx, height / (vby + band));
  const dx = (width - vbx * scale) / 2, dy = (height - (vby + band) * scale) / 2;
  const duration = fig.timeline?.duration ?? 0;
  const ms = t * duration;

  if (opts.background !== null) { ctx.fillStyle = opts.background ?? C.paper; ctx.fillRect(0, 0, width, height); }
  ctx.save();
  ctx.translate(dx, dy);
  ctx.beginPath(); ctx.rect(0, 0, vbx * scale, vby * scale); ctx.clip();
  ctx.scale(scale, scale);
  ctx.lineCap = "round"; ctx.lineJoin = "round";

  for (const { part, flat } of r.parts) {
    const s = stateAt(r, part, ms);
    if (s.hidden) continue;
    ctx.save();
    ctx.globalAlpha = s.opacity;
    if (s.translate[0] || s.translate[1]) ctx.translate(s.translate[0], s.translate[1]);
    if (s.rotate || s.scale !== 1) {
      ctx.translate(s.pivot[0], s.pivot[1]);
      if (s.rotate) ctx.rotate((s.rotate * Math.PI) / 180);
      if (s.scale !== 1) ctx.scale(s.scale, s.scale);
      ctx.translate(-s.pivot[0], -s.pivot[1]);
    }
    if (part.fill && s.reveal >= 1) {
      ctx.fillStyle = fill(part.role, part.emphasis);
      if (s.fillLevel < 1 && part.shape === "rect") {
        const g = part.geometry; const h = (g.h ?? 0) * Math.max(0, Math.min(1, s.fillLevel));
        ctx.save(); ctx.beginPath(); ctx.rect(g.x ?? 0, (g.y ?? 0) + (g.h ?? 0) - h, g.w ?? 0, h); ctx.clip();
        trace(ctx, flat.subpaths); ctx.fill(); ctx.restore();
      } else { trace(ctx, flat.subpaths); ctx.fill(); }
    }
    ctx.strokeStyle = stroke(part.role, part.emphasis);
    ctx.lineWidth = part.role === "structure" ? STYLE.structureStroke : STYLE.roleStroke;
    ctx.setLineDash(part.dashed ? [2.2, 1.6] : []);
    trace(ctx, s.reveal < 1 ? prefix(flat, s.reveal) : flat.subpaths);
    ctx.stroke();
    ctx.restore();
  }

  // flowAlong particles
  for (const f of r.flows) {
    if (stateAt(r, f.part, ms).hidden) continue;
    const start = f.track.start ?? 0, dur = f.track.duration ?? duration, end = start + dur;
    let alpha = 1;
    if (!(start <= 0 && end >= duration - 0.5)) {
      if (ms < start || ms > end) continue;
      const ramp = dur * 0.08;
      alpha = Math.min(Math.min(1, (ms - start) / ramp), Math.min(1, (end - ms) / ramp));
    }
    const progress = Math.min(1, Math.max(0, (ms - start) / dur));
    const offset = progress * (f.track.cycles ?? 1);
    const count = f.track.count ?? 6;
    ctx.save(); ctx.globalAlpha = alpha; ctx.fillStyle = particle(f.part.role);
    for (let i = 0; i < count; i++) {
      const p = pointAt(f.flat, ((i / count + offset) % 1) * f.flat.length);
      if (!p) continue;
      ctx.beginPath(); ctx.arc(p[0], p[1], STYLE.particleRadius, 0, Math.PI * 2); ctx.fill();
    }
    ctx.restore();
  }
  ctx.restore();

  // labels, un-scaled for crisp text
  const labels = fig.labels ?? [];
  if (labels.length) {
    const fontPx = Math.max(opts.labelMinPx ?? STYLE.labelMinPx, STYLE.labelSize * scale);
    ctx.save();
    ctx.font = `500 ${fontPx}px "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif`;
    ctx.textBaseline = "top";
    (ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = `${fontPx * STYLE.labelTracking}px`;
    ctx.fillStyle = C.inkSoft; ctx.strokeStyle = C.inkSoft; ctx.lineWidth = Math.max(0.8, STYLE.leaderStroke * scale);
    const map = ([x, y]: [number, number]): [number, number] => [dx + x * scale, dy + y * scale];
    const right = dx + vbx * scale;
    for (const l of labels) {
      const text = l.text.toUpperCase();
      const w = ctx.measureText(text).width;
      const [ax, ay] = map(l.anchor);
      ctx.fillText(text, ax + w > right ? ax - w : ax, ay);
      if (l.leader && l.leader.length >= 2) {
        ctx.beginPath();
        l.leader.forEach((p, i) => { const [x, y] = map(p); i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); });
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  // the caption: what this figure shows, centred in the band under the drawing, in ink
  if (fig.caption) {
    const minPx = (opts.labelMinPx ?? STYLE.labelMinPx) * (STYLE.captionMinPx / STYLE.labelMinPx);
    let fontPx = Math.max(minPx, STYLE.captionSize * scale);
    const font = (px: number) => `500 ${px}px "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif`;
    ctx.save();
    ctx.font = font(fontPx);
    // a long caption shrinks to fit before it is cut, as in the Dart painter
    const w = ctx.measureText(fig.caption.toUpperCase()).width * (1 + STYLE.labelTracking);
    if (w > vbx * scale) { fontPx = Math.max(minPx, (fontPx * vbx * scale) / w); ctx.font = font(fontPx); }
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    (ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = `${fontPx * STYLE.labelTracking}px`;
    ctx.fillStyle = C.ink;
    ctx.fillText(fig.caption.toUpperCase(), dx + (vbx * scale) / 2, dy + (vby + band / 2 + band * 0.08) * scale, vbx * scale);
    ctx.restore();
  }
}
